import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { PropTypes as MobxPropTypes, observer } from 'mobx-react'
import { GoogleMap } from '@react-google-maps/api'
import { childrenPropType } from '../../../../lib/propTypes'
import { useGoogleMap } from '../../../../shared_components/Hooks/useGoogleMap'
import { defaultZoom, commonGoogleMapOptions, DEFAULT_CENTER } from '../../../../shared_components/Map/constants'
import mapStyles from '../../../../shared_components/Map/mapStyles'
import useMarkersBounds from '../hooks/useMarkersBounds'
import { debounce } from '../../../../lib/utils/common'

const containerStyle = { height: '100%', position: 'relative', overflow: 'hidden' }

const Map = ({
  markers,
  mapBounds,
  onBoundsChanged,
  onZoomChanged,
  googleMapOptions = {},
  onSubmitSearch,
  children,
  mapStore,
  valueStateStore
}) => {
  const { map, isLoaded, onMapLoad, onMapUnmount } = useGoogleMap()

  const isFirstZoomEvent = React.useRef(true)

  const rotateControlOptions = global.google && global.google.maps.ControlPosition.RIGHT_BOTTOM

  const handleBoundsChanged = useCallback(() => {
    if (map && onBoundsChanged) {
      onBoundsChanged(map.getBounds())
    }
  }, [map])

  const handleZoomChanged = useCallback(debounce(() => {
    if (map) {
      const newZoom = map.getZoom()
      if (mapStore.zoom === newZoom) { return }

      onZoomChanged(newZoom)

      if (isFirstZoomEvent.current) {
        isFirstZoomEvent.current = false
      } else {
        // Prevent extra requests on multiple zooms
        if (!valueStateStore?.isTownChanged) {
          onSubmitSearch()
        }
        valueStateStore?.setTownChanged(false)
      }
    }
  }, 800), [map, onZoomChanged, mapStore.zoom, onSubmitSearch, valueStateStore?.isTownChanged])

  const handleDragEnd = useCallback(debounce(() => {
    if (map && onSubmitSearch) {
      valueStateStore?.setTownChanged(false)
      onSubmitSearch()
    }
  }, 800), [map, onSubmitSearch])

  useMarkersBounds({ map, mapBounds, markers, valueStateStore })

  return (
    isLoaded && (
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={DEFAULT_CENTER}
        tilt={0}
        zoom={defaultZoom}
        onLoad={onMapLoad}
        onUnmount={onMapUnmount}
        onBoundsChanged={handleBoundsChanged}
        onZoomChanged={handleZoomChanged}
        onDragEnd={handleDragEnd}
        panTo={DEFAULT_CENTER}
        options={{
          ...commonGoogleMapOptions,
          streetViewControl: false,
          styles: mapStyles,
          ...googleMapOptions,
          rotateControlOptions: { position: rotateControlOptions }
        }}
      >
        {children && children}
      </GoogleMap>
    )
  )
}

Map.propTypes = {
  markers: MobxPropTypes.arrayOrObservableArray,
  mapBounds: MobxPropTypes.objectOrObservableObject,
  onBoundsChanged: PropTypes.func,
  onZoomChanged: PropTypes.func.isRequired,
  googleMapOptions: PropTypes.object,
  onSubmitSearch: PropTypes.func,
  children: childrenPropType,
  mapStore: PropTypes.shape({
    zoom: PropTypes.number
  }).isRequired,
  valueStateStore: PropTypes.shape({
    setTownChanged: PropTypes.func.isRequired,
    isTownChanged: PropTypes.bool.isRequired
  })
}
export default observer(Map)
